import { useTranslation } from 'react-i18next';
import { SkeletonBlock } from '../Skeleton';
import { stableEmoji } from '../../types/passport';
import type { HistoryOrder } from '../../types/passport';

interface HistoryViewProps {
  historyOrders: HistoryOrder[];
  historyLoading: boolean;
  historyLoadingMore: boolean;
  historyTotal: number;
  historyPage: number;
  fetchHistory: (page: number, append: boolean) => void;
}

export default function HistoryView({
  historyOrders,
  historyLoading,
  historyLoadingMore,
  historyTotal,
  historyPage,
  fetchHistory,
}: HistoryViewProps) {
  const { t } = useTranslation();

  return (
    <div className="pp-history-view pp-tab-content">
      {historyLoading ? (
        Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="pp-history-order" style={{ pointerEvents: 'none' }}>
            <div className="pp-history-order-header">
              <SkeletonBlock width="140px" height="14px" radius="4px" />
              <SkeletonBlock width="78px" height="11px" radius="4px" />
            </div>
            <div className="pp-history-order-dishes">
              <SkeletonBlock width="85%" height="12px" radius="4px" />
            </div>
            <div className="pp-history-order-footer">
              <SkeletonBlock width="62px" height="13px" radius="4px" />
              <SkeletonBlock width="74px" height="11px" radius="4px" />
              <SkeletonBlock width="86px" height="20px" radius="99px" />
            </div>
          </div>
        ))
      ) : historyOrders.length === 0 ? (
        <div className="pp-history-empty">{t('passport.noHistory')}</div>
      ) : (
        <>
          {historyOrders.map(order => {
            const dishStr = order.items
              .map(it => `${it.name}${it.quantity > 1 ? ` ×${it.quantity}` : ''}`)
              .join(', ');
            const truncated = dishStr.length > 50 ? dishStr.slice(0, 47) + '…' : dishStr;
            const dateStr = new Date(order.createdAt).toLocaleDateString('ru-RU', {
              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
            });
            const pts = Math.floor(order.totalAmount / 10);
            const isCancelled = order.status === 'CANCELLED';
            return (
              <div key={order.id} className="pp-history-order">
                <div className="pp-history-order-header">
                  <span className="pp-history-order-rest">
                    {stableEmoji(order.restaurant.id)} {order.restaurant.name}
                  </span>
                  <span className="pp-history-order-date">{dateStr}</span>
                </div>
                <div className="pp-history-order-dishes">{truncated}</div>
                <div className="pp-history-order-footer">
                  <span className="pp-history-order-total">
                    {Math.round(order.totalAmount).toLocaleString('ru-RU')} ₸
                  </span>
                  {!isCancelled && (
                    <span className="pp-history-order-pts">
                      {t('passport.pointsEarned', { n: pts })}
                    </span>
                  )}
                  <span className={`pp-history-order-status${isCancelled ? ' pp-history-order-status--cancelled' : ''}`}>
                    {isCancelled
                      ? ` ${t('orders.statusCancelled')}`
                      : ` ${t('orders.statusCompleted')}`}
                  </span>
                </div>
              </div>
            );
          })}
          {historyOrders.length < historyTotal && (
            <button
              className="pp-load-more-btn"
              onClick={() => fetchHistory(historyPage + 1, true)}
              disabled={historyLoadingMore}
            >
              {historyLoadingMore ? t('common.loading') : t('passport.loadMore')}
            </button>
          )}
        </>
      )}
    </div>
  );
}
